import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { ECaseMatchStatus, TALENT_LOCATION } from "@/types/enum";

// Certificate names that require majorId and jobTypeId
export const CERTIFICATES_REQUIRING_MAJOR_JOB_TYPE = [
  "介護技能実習評価試験(専門級)の実技試験の合格証明書",
  "分野別技能検定（3級）または技能実習評価試験（専門級）の実技試験の合格証明",
  "技能実習生に関する評価調書",
];

export const checkMissingRequiredFields = (caseMatch: TCaseMatches, status?: number, exemptSpecifiedSkillCert = false): boolean => {
  const currentStatus = status ?? caseMatch.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;

  // Terminal statuses (red) don't need to check required fields
  const terminalStatuses = [
    ECaseMatchStatus.FAILED,
    ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    ECaseMatchStatus.OFFER_CANCELLED,
    ECaseMatchStatus.SELECTION_CANCELLED,
  ];

  if (terminalStatuses.includes(currentStatus)) {
    return false;
  }

  // Check required fields based on status
  const isStatusAtLeast = (minStatus: ECaseMatchStatus) => {
    return currentStatus >= minStatus;
  };

  // 推薦中 and above: requires 推薦日, documents, and conditionally majorId, jobTypeId, residenceCard
  if (isStatusAtLeast(ECaseMatchStatus.PROPOSING)) {
    if (!caseMatch.proposalDate) {
      return true;
    }
  }

  // 選考中 and above: requires 面接日
  if (isStatusAtLeast(ECaseMatchStatus.UNDER_SELECTION)) {
    if (!caseMatch.interviewDate) {
      return true;
    }
  }

  // 推薦中 and above: requires documents, and conditionally majorId, jobTypeId, residenceCard
  if (isStatusAtLeast(ECaseMatchStatus.PROPOSING)) {
    const talent = caseMatch.talent;
    if (talent) {
      if (!talent.hasLanguageCertificates) {
        return true;
      }

      // Check 日本語能力証明書ファイル
      if (!talent.hasJapaneseCertificateFile) {
        return true;
      }

      if (!exemptSpecifiedSkillCert) {
        // Check 特定技能合格証等 (specificSkillCertificateStr)
        if (!talent.specificSkillCertificateStr) {
          return true;
        }

        // Check 特定技能合格証等ファイル
        if (!talent.hasSpecifiedSkillCertificateFile) {
          return true;
        }
      }

      // Check if talent has selected certificates that require majorId/jobTypeId
      const requiresMajorAndJobType =
        talent.specificSkillCertificateStr &&
        CERTIFICATES_REQUIRING_MAJOR_JOB_TYPE.some((certName) => talent.specificSkillCertificateStr?.includes(certName));

      if (requiresMajorAndJobType) {
        // Check majorId (技能実習　職種)
        if (!talent.majorId) {
          return true;
        }
        // Check jobTypeId (作業)
        if (!talent.jobTypeId) {
          return true;
        }
      }

      // Check 在留カード（表・裏） for LOCAL talents
      if (talent.location === TALENT_LOCATION.LOCAL && !talent.hasResidenceCardFiles) {
        return true;
      }
    } else {
      return true;
    }
  }

  // 内定 and above: requires 企業内定日
  if (isStatusAtLeast(ECaseMatchStatus.OFFER)) {
    if (!caseMatch.companyOfferDate) {
      return true;
    }
  }

  // 内定受諾 and above: requires 内定受諾日, 企業入社日（予定）, 紹介料
  if (isStatusAtLeast(ECaseMatchStatus.OFFER_ACCEPTED)) {
    if (!caseMatch.offerAcceptDate) {
      return true;
    }
    if (!caseMatch.joinDateExpected) {
      return true;
    }
    if (caseMatch.salesFee === null || caseMatch.salesFee === undefined) {
      return true;
    }
    if (caseMatch.purchaseFee === null || caseMatch.purchaseFee === undefined) {
      return true;
    }
  }

  // 入社済み: requires 企業入社日
  if (currentStatus === ECaseMatchStatus.ALREADY_HIRED) {
    if (!caseMatch.companyJoiningDate) {
      return true;
    }
  }

  return false;
};
